/**
 * Purpose: Configuration constants for the classroom-based progression system.
 * Owns: Class sizes (seat counts and grid layouts), subject-to-size mapping,
 *       grading scale, honor titles, and schedule structure.
 * Notes: All game configuration is driven by these constants (CS-21).
 *        No magic numbers — every numeric value lives here or is derived from here.
 *        The Final Exam is always period 9 (the last period in every schedule).
 */

import type { Category, AgeGroup } from '../types/question';

// _______________________________________________
//              CLASS SIZE DEFINITIONS
// _______________________________________________
// Each class has a number of seats arranged in a grid.
// Seats represent the spaces a player must advance through to "pass" the class.

/** Shape of a class size configuration. */
export interface ClassSize {
  /** Total number of seats (spaces) in this class */
  seats: number;
  /** Grid layout: [rows, columns] */
  grid: [rows: number, cols: number];
}

/** Easy classes — fewer seats, quicker to pass. */
export const EASY_CLASS: ClassSize = { seats: 6, grid: [2, 3] };

/** Standard classes — moderate seat count. */
export const STANDARD_CLASS: ClassSize = { seats: 9, grid: [3, 3] };

/** Hard classes — more seats, longer to pass. */
export const HARD_CLASS: ClassSize = { seats: 12, grid: [3, 4] };

/** Final Exam — the ultimate challenge with the most seats. */
export const FINAL_EXAM: ClassSize = { seats: 20, grid: [4, 5] };

// _______________________________________________
//              SUBJECT-TO-SIZE MAPPING
// _______________________________________________
// Maps each school subject to its class size. Drives how many seats
// each class period contains. Easy subjects are shorter, hard subjects longer.

export const SUBJECT_CLASS_SIZE: Record<Category, ClassSize> = {
  Art: EASY_CLASS,
  Music: EASY_CLASS,
  English: STANDARD_CLASS,
  Geography: STANDARD_CLASS,
  History: STANDARD_CLASS,
  Sports: STANDARD_CLASS,
  Math: HARD_CLASS,
  Science: HARD_CLASS,
};

// _______________________________________________
//              FINAL EXAM SUBJECT LABEL
// _______________________________________________
// The Final Exam is not a regular subject — it pulls from all categories.
// This label is used for display and schedule building.

export const FINAL_EXAM_LABEL = 'Final Exam';

/** The period number assigned to the Final Exam (always last). */
export const FINAL_EXAM_PERIOD = 9;

/** Total number of class periods including the Final Exam. */
export const TOTAL_PERIODS = FINAL_EXAM_PERIOD;

// _______________________________________________
//              PURE CLASSROOM MODE — QUESTIONS PER SUBJECT
// _______________________________________________
// In Pure Classroom mode there are no seats or dice. Each period has a fixed
// number of questions. The player answers all of them, gets graded, and moves
// to the next period. These counts mirror the easy/standard/hard difficulty
// tiers of the subject class sizes.

/** Questions per subject in Pure Classroom mode (CS-21). */
export const PURE_CLASSROOM_QUESTIONS: Record<Category, number> = {
  Art: 2,
  Music: 2,
  English: 3,
  Geography: 3,
  History: 3,
  Sports: 3,
  Math: 4,
  Science: 4,
};

/** Number of questions in the Pure Classroom Final Exam (covers all subjects). */
export const PURE_CLASSROOM_FINAL_EXAM_QUESTIONS = 6;

// _______________________________________________
//              REGULAR SUBJECTS LIST
// _______________________________________________
// The 8 subjects that make up periods 1-8. Order here is the default
// schedule order; randomized schedules shuffle these before assignment.

export const REGULAR_SUBJECTS: Category[] = [
  'Math',
  'Science',
  'History',
  'English',
  'Art',
  'Geography',
  'Music',
  'Sports',
];

/** Number of regular class periods (excludes Final Exam). */
export const REGULAR_PERIOD_COUNT = REGULAR_SUBJECTS.length;

// _______________________________________________
//              GRADING SCALE
// _______________________________________________
// Letter grades based on percentage of questions answered correctly in a class.
// GPA points map letter grades to a 4.0 scale for GPA calculation.

export interface GradeThreshold {
  /** Minimum percentage (inclusive) to earn this grade */
  minPercent: number;
  /** Letter grade */
  letter: string;
  /** GPA points on a 4.0 scale */
  gpaPoints: number;
}

/**
 * Grading scale ordered from highest to lowest.
 * The first matching threshold (where percentage >= minPercent) is the grade.
 */
export const GRADING_SCALE: GradeThreshold[] = [
  { minPercent: 90, letter: 'A', gpaPoints: 4.0 },
  { minPercent: 80, letter: 'B', gpaPoints: 3.0 },
  { minPercent: 70, letter: 'C', gpaPoints: 2.0 },
  { minPercent: 60, letter: 'D', gpaPoints: 1.0 },
  { minPercent: 0, letter: 'F', gpaPoints: 0.0 },
];

// _______________________________________________
//              ATTENDANCE-BASED GRADING
// _______________________________________________
// Grade formula: grade = ATTENDANCE_BASE + (earnedPortion) - behaviorDock
// The attendance base is a free percentage for "showing up and playing."
// The remaining portion is earned through correct answers, with an optional
// knockdown multiplier when a hint was used.

/** Free percentage every player gets for "attendance and citizenship" (CS-20). */
export const ATTENDANCE_BASE_PERCENT = 50;

/**
 * Hint knockdown multipliers by age group (CS-21).
 * When a question is answered with help, its earned value is multiplied by
 * this factor. Younger kids get no penalty; adults get half credit.
 */
export const HINT_KNOCKDOWN_MULTIPLIER: Record<AgeGroup, number> = {
  'kids-3-5': 1.0,
  'kids-6-8': 0.9,
  'kids-9-12': 0.8,
  'teens-13-17': 0.65,
  'adults-18+': 0.5,
  'parents': 0.5,
};

// _______________________________________________
//              HONOR TITLES
// _______________________________________________
// Titles awarded at end of game based on final GPA.

export interface HonorTitle {
  /** Minimum GPA (inclusive) to earn this title */
  minGpa: number;
  /** The Latin honor or descriptive title */
  title: string;
}

/**
 * Honor titles ordered from highest to lowest.
 * The first matching title (where GPA >= minGpa) is awarded.
 */
export const HONOR_TITLES: HonorTitle[] = [
  { minGpa: 3.9, title: 'Summa Cum Laude' },
  { minGpa: 3.5, title: 'Magna Cum Laude' },
  { minGpa: 3.0, title: 'Cum Laude' },
  { minGpa: 1.0, title: 'Passed' },
  { minGpa: 0, title: 'Summer School' },
];

/** GPA target used to evaluate solo Honor Roll performance. */
export const SOLO_HONOR_ROLL_GPA_TARGET = 3.0;

// _______________________________________________
//              BEHAVIOR DOCK OPTIONS
// _______________________________________________
// Percentage deductions a host can apply for bad behavior during a class.
// Each option is a flat percentage subtracted from the class grade formula.

export interface BehaviorDockOption {
  /** Display label shown in the UI */
  label: string;
  /** Percentage points to dock (positive number subtracted from grade) */
  value: number;
}

/** Available behavior dock amounts the host can apply (CS-21). */
export const BEHAVIOR_DOCK_OPTIONS: BehaviorDockOption[] = [
  { label: '\u22125%', value: 5 },
  { label: '\u221210%', value: 10 },
  { label: '\u221220%', value: 20 },
];

// _______________________________________________
//              PLAYER LIMITS
// _______________________________________________

/** Minimum number of players (solo play is valid). */
export const MIN_PLAYERS = 1;

/** Maximum number of players in a game session. */
export const MAX_PLAYERS = 8;
